import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteWishlistItem,
  fetchWishlist,
} from "../redux/actions/wishlistAction";
import { RiDeleteBin6Line } from "react-icons/ri";
import { addToCartThunk } from "../redux/actions/cartActions";
import Modal from "../components/other/Modal";
import { useNavigate } from "react-router-dom";
import { checkStockAvailability } from "../redux/actions/stockActions";
import { toast } from "react-toastify";
import { Alert } from "../components/common/Alert";

const Wishlist = () => {
  const dispatch = useDispatch();
  const { items, loading, error } = useSelector((state) => state.wishlist);
  const user = useSelector((state) => state.auth?.user);
  const userType = useSelector((state) => state.auth?.user?.user?.user_type);
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subProducts, setSubProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [currentProductId, setCurrentProductId] = useState(null);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (user) {
      dispatch(fetchWishlist());
    }
  }, [dispatch, user]);

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setIsConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (deleteId !== null) {
      dispatch(deleteWishlistItem(deleteId));
      setDeleteId(null);
    }
    setIsConfirmOpen(false);
  };

  // Open modal with subProducts when "Open Modal" is clicked
  const handleAddToCartClick = (item) => {
    const subs = item.product?.SubProducts || [];

    if (subs.length > 0) {
      // initialize quantities to 1 for each sub-product
      const initQty = Object.fromEntries(
        subs.map((sub) => [sub.sub_pro_id, 1])
      );
      setQuantities(initQty);

      setSubProducts(subs);
      setCurrentProductId(item.product.pro_id);
      setIsModalOpen(true);
    } else {
      // no sub-products → add main product directly
      dispatch(
        addToCartThunk({
          product_id: item.product.pro_id,
          quantity: 1,
        })
      );
    }
  };

  // Quantity controls handlers
  const incrementQuantity = (sub) => {
    const sub_product_id = sub.sub_pro_id;
    const quantity = (quantities[sub_product_id] || 1) + 1;

    // First, log what we're sending

    dispatch(checkStockAvailability(sub_product_id, quantity))
      .then((response) => {
        // Log the entire response to see its structure

        // The response structure is likely different than expected
        // The data might be directly in response.data, not in response.payload.data
        const data = response.data || {};

        // Check if there's stock available
        if (
          data.data?.status === "out_of_stock" ||
          data.data?.available_stock < data.data?.required_quantity
        ) {
          toast.info("Insufficient Stock !!!");
        } else {
          // Stock is available, increment quantity
          setQuantities((prev) => ({
            ...prev,
            [sub_product_id]: quantity,
          }));
        }
      })
      .catch((error) => {
        // Log the error for debugging

        // Display a more specific error message if available
        const errorMessage =
          error.message || "Failed to check stock. Please try again.";
        toast.error(errorMessage);
      });
  };

  const decrementQuantity = (id) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: prev[id] > 1 ? prev[id] - 1 : 1,
    }));
  };

  // Add selected sub-product to cart and close modal
  const handleAddSubProductToCart = (sub) => {
    const sub_product_id = sub.sub_pro_id;
    const quantity = quantities[sub_product_id] || 1;

    dispatch(checkStockAvailability(sub_product_id, quantity))
      .then((response) => {
        // Log the entire response to see its structure

        // The response structure is likely different than expected
        // The data might be directly in response.data, not in response.payload.data
        const data = response.data || {};

        // Check if there's stock available
        if (
          data.data?.status === "out_of_stock" ||
          data.data?.available_stock < data.data?.required_quantity
        ) {
          Alert.ErrorAlert("Insufficient stock!");
        } else {
          // Stock is available, increment quantity
          dispatch(
            addToCartThunk({
              product_id: currentProductId,
              sub_product_id,
              quantity,
            })
          );
        }
      })
      .catch((error) => {
        // Log the error for debugging

        // Display a more specific error message if available
        const errorMessage =
          error.message || "Failed to check stock. Please try again.";
        toast.error(errorMessage);
      });
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "75vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading Profile...</span>
        </div>
      </div>
    );
  }

  if (error)
    return <div className="text-center my-5 text-red-500">{error}</div>;

  const validItems = Array.isArray(items)
    ? items.filter((item) => item && item.product)
    : [];

  if (!user) {
    return (
      <div className="flex flex-col justify-center mt-4 items-center h-64">
        <svg
          className="w-16 h-16 text-purple-400 mb-4"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.5 7.5a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM3.75 21a8.25 8.25 0 1116.5 0H3.75z"
          />
        </svg>
        <div className="text-gray-700 text-xl font-semibold mb-2">
          Your Wishlist is Waiting!
        </div>
        <div className="text-gray-500 mb-4 text-center max-w-xs">
          Please <span className="font-medium text-purple-600">log in</span> to
          view and manage your wishlist items, save favorites, and enjoy a
          personalized shopping experience.
        </div>
        <button
          onClick={() => navigate("/login")}
          className="px-6 py-2 rounded bg-purple-600 text-white font-semibold hover:bg-purple-700 transition"
        >
          Login to Continue
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-[50vh] p-4">
      {validItems.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {validItems.map((item) => {
            const product = item.product;
            if (!product) return null;
            return (
              <div
                key={item.id}
                className="size-full shadow p-3 rounded-xl flex flex-col"
              >
                <div className="w-full aspect-[3/4] overflow-hidden">
                  <img
                    loading="lazy"
                    src={product.product_primary_img || "/placeholder.jpg"}
                    alt={product.product_name}
                    className="object-cover size-full"
                  />
                </div>
                <div className="flex flex-col flex-1">
                  <h5 className="fw-bold text-lg lg:text-xl">
                    {product.product_name}
                  </h5>
                  <p className="text-gray-500 text-sm lg:text-base fw-semibold mt-2">
                    {product.product_sub_title}
                  </p>
                  <div className="flex flex-col lg:flex-row gap-1 lg:gap-3 lg:items-center lg:justify-between mt-2">
                    <p className="text-base font-semibold text-gray-700 whitespace-nowrap">
                      {userType === "vendor"
                        ? product.SubProducts[0]?.vendor_product_price
                        : product.SubProducts[0]?.customer_product_price}
                      <span className="text-base"> ₹</span>
                    </p>
                  </div>
                  <div className="flex-1 flex justify-between gap-1 lg:gap-3 items-end lg:justify-between mt-2">
                    <button
                      onClick={() => handleAddToCartClick(item)}
                      className="p-2 max-sm:flex-1 sm:w-2/3 theme-btn rounded-3xl text-xs sm:text-sm lg:text-base"
                    >
                      Add to Cart
                    </button>
                    <button
                      onClick={() => handleDeleteClick(item.id)}
                      className="text-red-600 text-base lg:text-xl p-2 rounded-lg hover:bg-red-500 hover:text-white"
                      aria-label={`Delete ${product.product_name} from wishlist`}
                    >
                      <RiDeleteBin6Line />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="my-5 flex justify-center w-full">
          <p className="text-gray-500">No items in your wishlist</p>
        </div>
      )}

      {/* Sub‑product selection modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Select Sub Product"
      >
        <div className="flex flex-col gap-4">
          {subProducts.map((sub, idx) => (
            <div
              key={idx}
              className="w-full border rounded-xl p-4 shadow hover:shadow-lg transition flex flex-row items-center gap-4"
            >
              {/* Image on the left */}
              <img
                loading="lazy"
                src={sub.sub_pro_img}
                alt={`sub-product-${idx}`}
                className="w-32 h-44 object-cover hover:scale-110 transition-transform ease-in-out duration-500 cursor-pointer rounded-lg flex-shrink-0"
              />

              {/* Details on the right */}
              <div className="flex flex-col justify-between flex-grow h-full">
                <div className="space-y-1">
                  <p>
                    <strong>Color:</strong> {sub.color_id}
                  </p>
                  <p>
                    <strong>Size:</strong> {sub.size}
                  </p>
                  {/* <p><strong>Location:</strong> {sub.location}</p> */}
                  {isAuthenticated ? (
                    <p>
                      <strong>Price:</strong> ₹
                      {userType === "vendor"
                        ? sub.set_price
                        : sub.customer_product_price}
                    </p>
                  ) : (
                    ""
                  )}
                </div>

                {/* Quantity controls */}
                <div className="self-start flex items-center mt-2 gap-2 ">
                  <div className="border border-gray-300 rounded-full overflow-hidden">
                    <button
                      onClick={() => decrementQuantity(sub.sub_pro_id)}
                      className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                      disabled={quantities[sub.sub_pro_id] <= 1}
                    >
                      -
                    </button>
                    <span className="px-4 py-1 select-none">
                      {quantities[sub.sub_pro_id] || 1}
                    </span>
                    <button
                      onClick={() => incrementQuantity(sub)}
                      className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                    >
                      +
                    </button>
                  </div>

                  {userType === "vendor" ? (
                    sub.selling_type == "set" ? (
                      <span className="ml-2">
                        {quantities[sub.sub_pro_id] <= 1 ? "Set" : "Sets"}{" "}
                      </span>
                    ) : (
                      <span className="ml-2">
                        {quantities[sub.sub_pro_id] <= 1 ? "Piece" : "Pieces"}
                      </span>
                    )
                  ) : null}
                </div>

                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                  {/* Note Text */}
                  {userType === "vendor" &&
                  sub.selling_type == "set" &&
                  sub.no_of_pcs ? (
                    <p className="italic text-sm text-red-600 font-semibold whitespace-nowrap min-w-[180px]">
                      {` ${quantities[sub.sub_pro_id] || 1} Set = ${
                        sub.no_of_pcs * (quantities[sub.sub_pro_id] || 1)
                      } pieces`}{" "}
                      (₹{sub.set_price * (quantities[sub?.sub_pro_id] || 1)})
                    </p>
                  ) : (
                    // Invisible div to take up the same space so button stays right-aligned
                    <div className="min-w-[180px]" />
                  )}

                  {/* Add Button */}
                  {isAuthenticated ? (
                    <div className="flex flex-col gap-1 w-full sm:w-fit">
                      <button
                        className="btn theme-outline-btn disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleAddSubProductToCart(sub)}
                        disabled={sub.no_of_stock <= 0}
                      >
                        Add Item
                      </button>

                      {sub.no_of_stock <= 0 && (
                        <p className="text-red-600 text-sm font-semibold">
                          Out of Stock
                        </p>
                      )}
                    </div>
                  ) : (
                    navigate("/login")
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal>
      {/* Confirm Delete modal */}
      <Modal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title="Remove from Wishlist?"
      >
        <p className="mb-6">Are you sure you want to delete this item?</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setIsConfirmOpen(false)}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={confirmDelete}
            className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Wishlist;
